import React from "react";

export default function PrivacyPolicyPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="privacy-policy-page">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Legal</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-2">Privacy Policy</h1>
      <p className="text-sm text-neutral-500 mb-10">Last updated: 25 July 2026</p>

      <div className="prose prose-neutral max-w-none space-y-8 text-neutral-700">
        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">1. Who we are</h2>
          <p>
            Zyro ("we", "us", or "our") is a hospitality booking platform operated by Zyro Hospitality
            (registered in India). We connect travellers with hotels for short stays and with property
            owners for long-term rentals. This policy explains what data we collect, why, and your rights.
          </p>
          <p>Contact: <a href="mailto:conect@zyroapp.in" className="text-gold underline">conect@zyroapp.in</a> · <a href="tel:+919262592844" className="text-gold underline">+91 92625 92844</a> · India</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">2. Information we collect</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li><b>Account data</b>: name, email, phone number, hashed password, role (guest / hotel partner / room owner).</li>
            <li><b>ID verification</b>: government-issued ID (Aadhaar / Voter ID / Passport / Driving License) — uploaded once, reviewed by our admins, then encrypted at rest.</li>
            <li><b>Booking &amp; rental data</b>: dates, guest count, property choice, chat with the host, payment reference IDs from Razorpay.</li>
            <li><b>Property data (for partners)</b>: title, address, photos, amenities, price, electricity bill copy (rental owners).</li>
            <li><b>Device &amp; usage</b>: IP, browser type, pages viewed, service-worker cache status. Used only for security and reliability.</li>
            <li><b>Location</b>: we ask for your city (not GPS) to show nearby stays. You can change it any time from the top bar.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">3. How we use your data</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Deliver the booking / rental service (search, book, pay, chat, check-in, invoice, refund).</li>
            <li>Verify identity and prevent fraud (KYC compliance under Indian law).</li>
            <li>Send transactional messages: OTP, booking confirmation, payment receipt, cancellation refund status. We do <b>not</b> send marketing SMS/email without a separate opt-in.</li>
            <li>Improve the product — aggregated, non-identifying analytics only.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">4. Who we share with</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li><b>Payment gateway</b>: Razorpay Software Pvt. Ltd. handles card / UPI / wallet processing. We never see or store your full card number.</li>
            <li><b>Hotels &amp; property owners</b>: they see your name, booking dates, and in-app chat only. Your phone/email are hidden — communication happens through Zyro's chat.</li>
            <li><b>Government / law enforcement</b>: only when compelled by a valid Indian legal order.</li>
            <li><b>Cloud infrastructure</b>: Emergent object storage (files) and MongoDB Atlas (records), both in India-region servers.</li>
          </ul>
          <p className="mt-3">We do <b>not</b> sell or rent your personal information to third parties. Ever.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">5. Data retention</h2>
          <p>
            KYC documents are retained for 5 years after your last booking, as required under Indian
            hospitality &amp; anti-money-laundering regulations. Booking records for 8 years for tax
            compliance. You may request deletion of chat and profile data at any time — booking / KYC
            records are anonymised where legally permitted.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">6. Your rights</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Access — request a copy of the data we hold about you.</li>
            <li>Correction — update your profile from the app or contact support.</li>
            <li>Deletion — email <a href="mailto:conect@zyroapp.in" className="text-gold underline">conect@zyroapp.in</a>; we respond within 30 days.</li>
            <li>Withdraw consent — you can disable OTP, chat, or delete your account.</li>
            <li>Grievance officer — Zyro Grievance Cell, India · <a href="mailto:conect@zyroapp.in" className="text-gold underline">conect@zyroapp.in</a> · <a href="tel:+919262592844" className="text-gold underline">+91 92625 92844</a></li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">7. Security</h2>
          <p>
            All traffic is TLS-encrypted. Passwords are hashed with bcrypt. OTPs are hashed and expire in
            5 minutes with rate-limits. Payment signatures are verified with HMAC-SHA256. Sensitive
            documents are stored in access-controlled object storage.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">8. Children</h2>
          <p>Zyro is not intended for anyone under 18. We do not knowingly collect data from minors.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">9. Cookies &amp; PWA</h2>
          <p>
            We use one httpOnly session cookie for authentication. Our service worker caches static assets
            for offline reliability — it does not track you. You can clear it from your browser at any time.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">10. Changes to this policy</h2>
          <p>
            If we make material changes we will notify you in-app and via email 14 days before they take
            effect. Continued use of Zyro after the effective date means you accept the updated policy.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-midnight mb-3">11. Contact</h2>
          <p>
            Zyro Hospitality<br />
            India<br />
            <a href="mailto:conect@zyroapp.in" className="text-gold underline">conect@zyroapp.in</a><br />
            <a href="tel:+919262592844" className="text-gold underline">+91 92625 92844</a>
          </p>
        </section>
      </div>
    </div>
  );
}
